import React, { useEffect, useRef } from "react";
import Icon from "assets/icons";
import classNames from "classnames";
import { TableStorage } from "./tableStorage";
import { defaultSortTypes } from "./sortTypes";
import { useCopies } from "shared/hooks/useCopies";
import AutoSizer from "react-virtualized-auto-sizer";
import { AppType, TableType } from "shared/utils/enums";
import { getEnvironmentInfo } from "shared/utils/config";
import { TableProps, TableStateCustom } from "./table.types";
import { DefaultColumnFilter } from "./filters";
import { TABLE_MAX_WIDTH, TABLE_MIN_WIDTH, TABLE_ROW_DEFAULT_HEIGHT, TABLE_WIDTH } from "shared/utils/constants";
import { FixedSizeList, VariableSizeList } from "react-window-dynamic";
import {
  useTable,
  useFlexLayout,
  useSortBy,
  useResizeColumns,
  useFilters,
  useGlobalFilter,
  useExpanded
} from "react-table";
import { Virtuoso } from "react-virtuoso";
import NoDataFound from "shared/components/noDataFound";
import { TABLE_DEFAULT_OPTIONS_HEIGHT } from "shared/utils/styleConstants";
import SearchWrapper from "shared/components/searchWrapper";

const Config = {
  itemSize: TABLE_ROW_DEFAULT_HEIGHT
};

/**
 * @param {originalRow} originalRow
 * Extract and Id for the row in table
 */
function defaultGetRowId(originalRow: any) {
  return originalRow.id;
}
function defaultTableName() {
  return `table-${window.location.pathname}`;
}

const noop = () => {};

const CoreInitialTableState = {
  sortBy: [
    {
      id: "name",
      desc: false
    }
  ]
};

function getInitialTableStateFromStorage(tableName: string, initialState: TableStateCustom) {
  let savedState;
  try {
    savedState = TableStorage.getTableState(tableName);
  } catch (e) {
    savedState = initialState;
  }

  return {
    sortBy: savedState.sortBy || initialState.sortBy || CoreInitialTableState.sortBy,
    hiddenColumns: savedState.hiddenColumns || initialState.hiddenColumns || [],
    isSearchable: !!savedState.isSearchable
  };
}

const defaultItemSize = (row: any) => {
  return Config.itemSize;
};

const defaultColumn = {
  Filter: DefaultColumnFilter,
  minWidth: TABLE_MIN_WIDTH, // minWidth is only used as a limit for resizing
  width: TABLE_WIDTH, // width is used for both the flex-basis and flex-grow
  maxWidth: TABLE_MAX_WIDTH // maxWidth is only used as a limit for resizing
};
export function Table<T extends object>({
  name = defaultTableName(),
  columns,
  data,
  config = Config,
  tableType = TableType.DYNAMIC,
  getRowProps = noop,
  getCellProps = noop,
  getItemSize = defaultItemSize,
  autoResetSortBy = false,
  autoResetFilters = false,
  autoResetGlobalFilter = false,
  getRowId = defaultGetRowId,
  initialTableState = CoreInitialTableState,
  fixedColumns = [],
  tableRef = { current: null },
  disableFilters = false,
  noRecordMessage,
  noRecordInfo,
  optionsHeight = TABLE_DEFAULT_OPTIONS_HEIGHT,
  showResultCount = true,
  readOnly = false,
  additionalRowClassName
}: TableProps<T>) {
  const copies = useCopies();
  const appType = getEnvironmentInfo().appType;
  const initialTableStateFromStorage = React.useMemo(() => {
    return getInitialTableStateFromStorage(name, initialTableState);
    // when data changes we reset the table state from storage, since data is not directly used here ignoring deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, initialTableState, data, columns, showResultCount, additionalRowClassName]);

  const [isSearchable, setIsSearchable] = React.useState(() => initialTableStateFromStorage.isSearchable);
  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
    totalColumnsWidth,
    getToggleHideAllColumnsProps,
    setHiddenColumns,
    setGlobalFilter,
    state,
    setAllFilters,
    setFilter
  } = useTable<T>(
    {
      columns,
      data,
      defaultColumn,
      sortTypes: defaultSortTypes,
      autoResetSortBy,
      autoResetFilters,
      getRowId,
      disableFilters,
      initialState: initialTableStateFromStorage,
      autoResetGlobalFilter
    },
    useFlexLayout,
    useResizeColumns,
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded
  );

  React.useEffect(() => {
    TableStorage.setTableState(name, { hiddenColumns: state.hiddenColumns, sortBy: state.sortBy, isSearchable });
  }, [state.hiddenColumns, name, state.sortBy, isSearchable]);

  React.useImperativeHandle(tableRef, () => {
    return {
      setHiddenColumns,
      getToggleHideAllColumnsProps,
      allColumns,
      fixedColumns,
      SearchBox: <SearchWrapper searchChangeHandler={setGlobalFilter} placeholder={copies.get("type.to.search")} />,
      setIsSearchable,
      isSearchable,
      setAllFilters,
      setFilter
    };
  });

  const tableBodyComponent = React.useMemo(() => {
    function RenderRow({ index, style, forwardedRef }: any) {
      const row = rows[index];
      prepareRow(row);
      return (
        <div
          {...row.getRowProps({
            style,
            ...getRowProps(row)
          })}
          ref={forwardedRef || null}
          className={classNames("tr", totalColumnsWidth, additionalRowClassName)}>
          {row.cells.map(cell => {
            return (
              <div className="td" {...cell.getCellProps(getCellProps(cell))}>
                {cell.render("Cell")}
              </div>
            );
          })}
        </div>
      );
    }

    if (!rows.length) {
      return (
        <div className="table_noDataFound">
          <NoDataFound label={noRecordMessage && noRecordMessage} infoText={noRecordInfo && noRecordInfo} />
        </div>
      );
    }

    switch (tableType) {
      case TableType.FIXED:
        return (
          <AutoSizer>
            {({ width, height }) => {
              return (
                <FixedSizeList
                  height={height}
                  itemCount={rows.length}
                  itemSize={config.itemSize}
                  width={width}
                  key={rows.length}
                  className="tbody_list">
                  {RenderRow}
                </FixedSizeList>
              );
            }}
          </AutoSizer>
        );

      case TableType.VARIABLE:
        const _getItemSize = (index: number) => getItemSize(rows[index]);
        return (
          <AutoSizer>
            {({ width, height }) => {
              return (
                <VariableSizeList
                  height={height}
                  itemCount={rows.length}
                  itemSize={_getItemSize}
                  width={width}
                  key={rows.length}
                  className="tbody_list">
                  {RenderRow}
                </VariableSizeList>
              );
            }}
          </AutoSizer>
        );
      case TableType.DYNAMIC:
        return (
          <AutoSizer>
            {({ width, height }) => {
              return (
                <Virtuoso
                  className="tbody_list"
                  style={{ width, height }}
                  totalCount={rows.length}
                  components={{
                    Footer
                  }}
                  itemContent={index => {
                    const row = rows[index];
                    prepareRow(row);
                    return (
                      <div
                        data-testid={`table-row-${index}`}
                        {...row.getRowProps({
                          ...getRowProps(row)
                        })}
                        className={classNames("tr", totalColumnsWidth, additionalRowClassName)}>
                        {row.cells.map(cell => {
                          return (
                            <div className="td" {...cell.getCellProps(getCellProps(cell))}>
                              {cell.render("Cell")}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }}
                />
              );
            }}
          </AutoSizer>
        );
      default:
        break;
    }
  }, [
    rows,
    tableType,
    prepareRow,
    getRowProps,
    totalColumnsWidth,
    additionalRowClassName,
    getCellProps,
    noRecordMessage,
    noRecordInfo,
    config.itemSize,
    getItemSize
  ]);

  return (
    <div
      className={classNames("table_wrapper", { readOnly: readOnly })}
      style={{ height: `calc(100% - ${optionsHeight}px)` }}>
      <div className="table_overflow" style={{ height: showResultCount ? `calc(100% - 24px)` : `100%` }}>
        <div {...getTableProps()} style={{ minWidth: totalColumnsWidth }} className="table">
          <div className="thead">
            {/* Add Headers */}
            {headerGroups.map(headerGroup => (
              <div {...headerGroup.getHeaderGroupProps()} className="tr">
                {headerGroup.headers.map(column => (
                  <div
                    {...column.getHeaderProps()}
                    className={classNames("th", {
                      activeFilter: column.filterValue
                    })}>
                    <div {...column.getSortByToggleProps()} className="thead_column_content" data-testid={column.id}>
                      <span>{column.render("Header")}</span>
                      <div className="thead_column_sortIcon">
                        {/* Add a sort direction indicator */}
                        {column.isSorted ? (
                          <React.Fragment>
                            {appType === AppType.CONSOLE ? (
                              <span>{column.isSortedDesc ? <Icon.ExpandMore /> : <Icon.ExpandLess />}</span>
                            ) : (
                              <span>{column.isSortedDesc ? <Icon.SortDown /> : <Icon.SortUp />}</span>
                            )}
                          </React.Fragment>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                    {/* Render the columns filter UI */}
                    {column.canFilter && isSearchable && (
                      <div className="thead_column_filter slide slideDownAnimation">{column.render("Filter")}</div>
                    )}
                    {/* Add Resizing */}
                    <div {...column.getResizerProps()} className={`resizer ${column.isResizing ? "isResizing" : ""}`} />
                  </div>
                ))}
              </div>
            ))}
          </div>
          {/* Table Body */}
          <div className={classNames("tbody", { searchActive: isSearchable })}>{tableBodyComponent}</div>
        </div>
      </div>
      {showResultCount && (
        <p className="resultCount">
          {copies.get("results")} : {rows.length}
        </p>
      )}
    </div>
  );
}

export function useTableOptionsHeight(key: any) {
  const tableOptionsMeasurementRef = useRef<HTMLDivElement>(null);
  const [optionsHeight, setOptionsHeight] = React.useState(TABLE_DEFAULT_OPTIONS_HEIGHT);

  useEffect(() => {
    if (tableOptionsMeasurementRef.current) {
      setOptionsHeight(tableOptionsMeasurementRef.current?.offsetHeight + 4);
    }
  }, [key]);

  return {
    tableOptionsMeasurementRef,
    optionsHeight
  };
}

function Footer() {
  return (
    <div
      style={{
        paddingBottom: 10
      }}></div>
  );
}
